import { Dispatch, MouseEvent, SetStateAction, useCallback } from 'react';
import { Connection, Edge } from 'reactflow';
import { CanvasElementState } from '@components/MainStage/canvasStates';
import { clearMultiSelection } from '@components/MainStage/clearMultiSelection';
import { getFocusOnState } from '@components/MainStage/focusOn.store';
import { getHoverState } from '@components/MainStage/hover.store';
import useStore from '@components/MainStage/store';
import { MenuState } from '@components/MainStage/types';
import { ProjectDrawerTypes } from '@constants/entities/drawers';
import { ReactFlowId } from '@constants/entities/reports';
import { useProject } from '@context/Project';
import NodesRow from '@utils/canvas/NodesRow';
import { isElementLocal } from '@utils/drawers';

const useEdgeHandlers = ({
  menu,
  closeMenu,
  openMenu,
  setMenu,
}: {
  menu: MenuState | null;
  closeMenu: () => void;
  openMenu: () => void;
  setMenu: Dispatch<SetStateAction<MenuState | null>>;
}) => {
  const { setDrawer } = useProject();
  const { clearLocalEdges, clearLocalNodes, onConnect, selectEdge } =
    useStore();

  const clearLocalEntities = useCallback(() => {
    clearLocalEdges();
    clearLocalNodes();
  }, []);

  const onEdgeAdded = useCallback((connection: Connection) => {
    clearLocalEntities();

    const createdEdgeId = onConnect(connection);

    if (createdEdgeId) {
      selectEdge(createdEdgeId);
      setDrawer({ type: ProjectDrawerTypes.AddRelationship });
    }
  }, []);

  const onEdgeClick = useCallback(
    (_: MouseEvent, edge: Edge) => {
      if (edge.data?.isPhantom) return;

      const focus = getFocusOnState();
      if (
        focus.sourceId &&
        !(focus.isFocused(edge.source) && focus.isFocused(edge.target))
      )
        return;

      if (menu) closeMenu();
      clearLocalEntities();

      // A plain relationship click exits any active multi-selection.
      clearMultiSelection();

      selectEdge(edge.id);
      setDrawer({ type: ProjectDrawerTypes.EditRelationship });
    },
    [menu],
  );

  const onEdgeContextMenu = useCallback((event: MouseEvent, edge: Edge) => {
    event.preventDefault();
    if (isElementLocal(edge.id) || edge.data?.isPhantom) return;

    const focus = getFocusOnState();
    if (
      focus.sourceId &&
      !(focus.isFocused(edge.source) && focus.isFocused(edge.target))
    )
      return;

    const pane = document
      .querySelector(`#${ReactFlowId}`)
      ?.getBoundingClientRect();

    selectEdge(edge.id);

    if (pane) {
      setMenu({
        id: edge.id,
        type: 'edge',
        top: event.clientY - pane.top - NodesRow.Height / 2,
        left: event.clientX - pane.left,
      });

      openMenu();
    }
  }, []);

  const onEdgeMouseEnter = useCallback((_: MouseEvent, edge: Edge) => {
    if (edge.data?.canvas?.state === CanvasElementState.Disabled) return;

    getHoverState().setHoveredEdge({
      id: edge.id,
      source: edge.source,
      target: edge.target,
    });
  }, []);

  const onEdgeMouseLeave = useCallback(() => {
    getHoverState().setHoveredEdge(null);
  }, []);

  return {
    onEdgeAdded,
    onEdgeClick,
    onEdgeContextMenu,
    onEdgeMouseEnter,
    onEdgeMouseLeave,
  };
};

export default useEdgeHandlers;
